import React from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import classNames from 'classnames';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import s from './Themes.css';
import Link from '../../components/Link';
import ThemeThumb from '../../components/Common/ThemeThumb/ThemeThumb';
import { MADES, PER_PAGE, SORTS, pageNumbers, themesUrl } from './themesQuery';

/** Подпись к числу тем при включённом фильтре по версии */
const MADE_SUMMARY = {
	new: 'themes made in New Tab Studio 4.0',
	classic: 'themes made before 4.0',
};

/**
 * Список пользовательских тем.
 *
 * Раньше здесь был бесконечный скролл на событии scroll. Для человека это
 * терпимо, для робота — нет: он не скроллит и видел первые двадцать тем из
 * четырёхсот. Теперь страницы обычные, со ссылками, и всё отдаётся с сервера.
 *
 * Состояние живёт только в URL: страница, сортировка, поиск и версия. Поэтому
 * ссылку на любую выборку можно отправить, сохранить и открыть заново.
 */
function Themes({ themes, total, page, sort, search, made }) {
	const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
	const from = (page - 1) * PER_PAGE + 1;
	const to = Math.min(page * PER_PAGE, total);

	return (
		<div className={s.root}>
			<Container maxWidth="md" disableGutters>
				<div className={s.controls}>
					<form
						className={s.searchForm}
						method="get"
						action="/themes"
					>
						<input
							className={s.searchInput}
							type="search"
							name="q"
							defaultValue={search}
							placeholder="Search themes, try “exam”, “pixel”, “minimal”"
							aria-label="Search themes"
							maxLength={60}
						/>
						{/* сортировку переносим в запрос, чтобы поиск её не сбрасывал */}
						{sort !== 'popular' && (
							<input type="hidden" name="sort" value={sort} />
						)}
						{made && (
							<input type="hidden" name="made" value={made} />
						)}
					</form>

					<div className={s.sorts}>
						{SORTS.map(option => (
							<Link
								key={option.key}
								className={classNames(
									s.sort,
									option.key === sort && s.sortActive,
								)}
								to={themesUrl({
									sort: option.key,
									search,
									made,
								})}
							>
								{option.label}
							</Link>
						))}
					</div>
				</div>

				{/* В какой версии расширения сделана тема: новые дизайны 4.0 или старые */}
				<nav className={s.mades} aria-label="Filter by version">
					{MADES.map(option => (
						<Link
							key={option.key || 'all'}
							className={classNames(
								s.made,
								option.key === made && s.madeActive,
							)}
							to={themesUrl({ sort, search, made: option.key })}
						>
							{option.label}
						</Link>
					))}
				</nav>

				<p className={s.summary}>
					{total > 0 ? (
						<React.Fragment>
							{search ? (
								<React.Fragment>
									{total} themes match &ldquo;{search}&rdquo;
								</React.Fragment>
							) : (
								<React.Fragment>
									{total}{' '}
									{MADE_SUMMARY[made] ||
										'themes shared by users'}
								</React.Fragment>
							)}
							{totalPages > 1 && (
								<React.Fragment>
									{' '}
									· showing {from}–{to}
								</React.Fragment>
							)}
						</React.Fragment>
					) : (
						'Nothing here'
					)}
				</p>

				{themes.length === 0 ? (
					<div className={s.empty}>
						{search ? (
							<p>
								No themes match &ldquo;{search}&rdquo;. Try a
								shorter word, titles are short, and most of them
								are one or two words.
							</p>
						) : (
							<p>
								No themes here yet. Build a page with the new
								designs and share it from the extension’s
								settings: it’ll be the first one in this list.
							</p>
						)}
						<p>
							<Link to="/themes">Back to all themes</Link>
						</p>
					</div>
				) : (
					<Grid container>
						{themes.map(item => (
							<Grid item xs={12} sm={6} md={4} key={item.id}>
								<ThemeThumb theme={item} />
							</Grid>
						))}
					</Grid>
				)}

				{totalPages > 1 && (
					<nav className={s.pager} aria-label="Pagination">
						{page > 1 && (
							<Link
								className={s.page}
								to={themesUrl({
									page: page - 1,
									sort,
									search,
									made,
								})}
								rel="prev"
							>
								← Prev
							</Link>
						)}

						{pageNumbers(page, totalPages).map((number, i) =>
							number === null ? (
								<span
									className={s.gap}
									// eslint-disable-next-line react/no-array-index-key
									key={`gap-${i}`}
								>
									…
								</span>
							) : (
								<Link
									key={number}
									className={classNames(
										s.page,
										number === page && s.pageCurrent,
									)}
									to={themesUrl({
										page: number,
										sort,
										search,
										made,
									})}
								>
									{number}
								</Link>
							),
						)}

						{page < totalPages && (
							<Link
								className={s.page}
								to={themesUrl({
									page: page + 1,
									sort,
									search,
									made,
								})}
								rel="next"
							>
								Next →
							</Link>
						)}
					</nav>
				)}
			</Container>
		</div>
	);
}

Themes.propTypes = {
	// eslint-disable-next-line react/forbid-prop-types
	themes: PropTypes.array.isRequired,
	total: PropTypes.number.isRequired,
	page: PropTypes.number.isRequired,
	sort: PropTypes.string.isRequired,
	search: PropTypes.string,
	made: PropTypes.string,
};

Themes.defaultProps = {
	search: '',
	made: '',
};

export default withStyles(s)(Themes);
